import { useRef, useEffect } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';

import { useLandingContext } from 'hb-react/frontoffice/apps/LiveFeed/contexts/LandingContext';

import type { AttachedImageFittingProps } from './interfaces';

export const AttachedImageFitting = ({
  breakpoint,
  fittedHeights,
  height,
  image,
  isSingle,
  maxHeight,
  storeFittedHeights,
}: AttachedImageFittingProps) => {
  const { measure } = useLandingContext();

  const imgRef = useRef<HTMLImageElement>(null);

  const onLoad = () => {
    if (imgRef.current && fittedHeights[image.id]?.[breakpoint] === undefined) {
      let tempHeight: number | { _: number; m: number } = height;

      if (isSingle) {
        const fittedHeight = imgRef.current.height;

        tempHeight = fittedHeight > maxHeight ? maxHeight : fittedHeight;
      }

      const newHeight = {
        ...fittedHeights[image.id],
        [breakpoint]: tempHeight,
      };

      storeFittedHeights({
        ...fittedHeights,
        [image.id]: newHeight,
      });

      setTimeout(() => {
        try {
          measure();
          // eslint-disable-next-line no-empty
        } catch (_err) {}
      }, 800);
    }
  };

  useEffect(() => {
    if (imgRef.current?.complete) {
      onLoad();
    }
  }, []);

  /**
   * We add this container to fit the image.
   * We display this container until the image is loaded.
   * */
  return (
    <ContainerImage height={isSingle ? `${maxHeight}px` : height}>
      {/**
       * We get the height of the fitted image.
       * We store the fitted height in the session storage object.
       * The height value is used to:
       *   Set the needed height while the image is loading.
       *   It is very important to avoid glitch effect on scrolling posts.
       * */}
      <FitToDiv
        alt=""
        data-testid={`attached-image-id:${image.id}`}
        onLoad={onLoad}
        ref={imgRef}
        // Fallback in case if image is returned in old LF image format
        src={image.large_url ?? image.default_url}
      />
    </ContainerImage>
  );
};

const ContainerImage = styled(Box)`
  display: block;
  width: 100%;
`;

const FitToDiv = styled.img`
  max-height: 100%;
  max-width: 100%;
  object-fit: contain;
  opacity: 0;
`;
